import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import * as Sentry from "@sentry/react";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import Auth from "../../Auth";
import { useToast } from "../../components/ToastWrapper";
import { SudoButton } from "../../sudokit/components/SudoButton";

interface Signal {
  id: string;
  name: string;
  source?: string;
  available: boolean;
  data_points?: number;
}

interface StreamChunk {
  type?: string;
  text?: string;
  signals?: Signal[];
  insufficient_data?: boolean;
  error?: string;
  insightId?: string;
  generatedBy?: string;
  createdAt?: string;
}

interface InsightRecord {
  id: string;
  userId: string;
  content: string;
  signalsSnapshot: Signal[] | null;
  generatedBy: string;
  modelVersion: string;
  createdAt: string;
}

interface UserInsightsProps {
  user: {
    uid: string;
    displayName?: string;
    email?: string;
  } | null;
}

const UserInsights: React.FC<UserInsightsProps> = ({ user }) => {
  const confirmAction = useConfirmation(user?.uid);
  const [loading, setLoading] = useState(false);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState<InsightRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [insufficientData, setInsufficientData] = useState(false);
  const [history, setHistory] = useState<InsightRecord[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isRecentInsight, setIsRecentInsight] = useState(false);

  const { setToast } = useToast();

  const fetchHistory = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const { userInsights } = await Auth.fetchWithIdToken(`/api/admin/userInsights/${user.uid}/list`);
      setHistory(userInsights || []);
    } catch (err) {
      Sentry.captureException(err, { tags: { context: "userInsights:loadHistory" } });
    }
  }, [user?.uid]);

  // Auto-load the most recent insight on mount
  useEffect(() => {
    if (!user?.uid) {
      setLoadingLatest(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { userInsight, isRecent } = await Auth.fetchWithIdToken(`/api/admin/userInsights/${user.uid}/latest`);
        if (cancelled) return;
        if (userInsight) {
          setSelectedInsight(userInsight);
          setIsRecentInsight(!!isRecent);
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { context: "userInsights:loadLatest" } });
      } finally {
        if (!cancelled) setLoadingLatest(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const handleGenerate = useCallback(async () => {
    if (!user?.uid) {
      setToast({
        type: "error",
        message: "User ID not found"
      });
      return;
    }
    const runConfirmedAction = async () => {
      setLoading(true);
      setError(null);
      setInsufficientData(false);
      setSelectedInsight(null);
      try {
        let summaryText = "";
        let signals: Signal[] = [];
        let insightId = "";
        let generatedBy = "";
        let createdAt = "";
        await Auth.streamWithIdToken(`/api/admin/user/${user.uid}/insights/generate`, {}, null, (chunk: StreamChunk) => {
          if (chunk.type === "error" && chunk.error) {
            setError(chunk.error);
          }
          if (chunk.text) {
            summaryText += chunk.text;
          }
          if (chunk.signals) {
            signals = chunk.signals;
          }
          if (chunk.insufficient_data) {
            setInsufficientData(true);
          }
          if (chunk.insightId) insightId = chunk.insightId;
          if (chunk.generatedBy) generatedBy = chunk.generatedBy;
          if (chunk.createdAt) createdAt = chunk.createdAt;
        }, () => {
          setLoading(false);
          if (summaryText) {
            setSelectedInsight({
              id: insightId,
              userId: user.uid,
              content: summaryText,
              signalsSnapshot: signals.length > 0 ? signals : null,
              generatedBy,
              modelVersion: "",
              createdAt
            });
            setIsRecentInsight(true);
          }
          // Refresh history if it's currently visible
          if (showHistory) {
            fetchHistory();
          }
        }, (err: Error) => {
          Sentry.captureException(err);
          setError("Failed to generate insights: " + (err.message || "Unknown error"));
          setLoading(false);
        });
      } catch (err) {
        Sentry.captureException(err);
        setError("Failed to generate insights: " + ((err as Error).message || "Unknown error"));
        setLoading(false);
      }
    };
    if (selectedInsight && isRecentInsight) {
      const dateStr = selectedInsight.createdAt ? ` (generated ${moment(selectedInsight.createdAt).fromNow()}) ago` : "";
      await confirmAction({
        title: "Regenerate insight",
        message: `An insight already exists for this user${dateStr}. Regenerating uses resources — are you sure you want to continue?`,
        actionLabel: "Regenerate",
        destructive: false
      }, runConfirmedAction);
    } else {
      await runConfirmedAction();
    }
  }, [user?.uid, setToast, showHistory, fetchHistory, selectedInsight, isRecentInsight, confirmAction]);

  const handleToggleHistory = useCallback(() => {
    if (!showHistory && history.length === 0) {
      fetchHistory();
    }
    setShowHistory((prev) => !prev);
  }, [showHistory, history.length, fetchHistory]);

  const handleSelectInsight = useCallback((insight: InsightRecord) => {
    setInsufficientData(false);
    setError(null);
    setSelectedInsight(insight);

  }, []);

  const hasResults = selectedInsight !== null;
  return (
    <div className="flex flex-col gap-3 p-4 mb-5 bg-gray-50 border border-gray-200 rounded-lg">
      <div className="flex justify-between items-center">
        <h3 className="m-0 text-sm font-semibold text-gray-700">User Insights</h3>
        <SudoButton
          onClick={handleGenerate}
          disabled={loading}
          variant="primary"
          size="small"
          state={loading ? "loading" : "default"}
        >
          {loading ? "Generating..." : hasResults ? "Regenerate" : "Generate User Insights"}
        </SudoButton>
      </div>

      {loadingLatest && !selectedInsight && <div className="text-xs text-gray-400 p-2 text-center">Loading...</div>}

      {loading && !selectedInsight && <div className="text-sm text-gray-500 animate-pulse p-4 text-center font-mono">Analyzing user data...</div>}

      {error && <div className="p-3 text-sm text-red-800 bg-red-50 rounded-md">{error}</div>}

      {insufficientData && selectedInsight && <div className="p-3 text-sm text-amber-800 bg-amber-50 rounded-md">{selectedInsight.content}</div>}

      {!insufficientData && selectedInsight && <div className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">{selectedInsight.content}</div>}

      {selectedInsight && (selectedInsight.createdAt || selectedInsight.generatedBy) && (
        <div className="text-xs text-gray-400">
          {selectedInsight.createdAt && <>Generated {moment(selectedInsight.createdAt).fromNow()} ago</>}
          {selectedInsight.createdAt && selectedInsight.generatedBy && " · "}
          {selectedInsight.generatedBy && <>by {selectedInsight.generatedBy}</>}
        </div>
      )}

      {selectedInsight && selectedInsight.signalsSnapshot && selectedInsight.signalsSnapshot.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1.5 pt-3 border-t border-gray-100">
            {selectedInsight.signalsSnapshot.map((signal) => (
              <span
                key={signal.id}
                className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${signal.available ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}
              >
                {signal.name}
                {signal.available && signal.data_points !== undefined && signal.data_points > 0 && <span className="text-[10px] opacity-80">({signal.data_points})</span>}
                {!signal.available && <span className="text-[10px] opacity-80">N/A</span>}
              </span>
            ))}
          </div>
        </>
      )}

      {hasResults && (
        <div className="pt-2 border-t border-gray-100">
          <button
            onClick={handleToggleHistory}
            className="flex items-center gap-1 p-0 text-xs text-gray-500 bg-transparent border-none cursor-pointer hover:text-gray-700"
          >
            {showHistory ? "▼" : "▶"} {showHistory ? "Hide" : "View"} past insights
          </button>

          {showHistory && (
            <div className="mt-2 max-h-72 overflow-y-auto flex flex-col gap-2">
              {history.length === 0 ? (
                <div className="text-xs text-gray-400 p-2">No previous insights found.</div>
              ) : (
                history.map((insight) => (
                  <div
                    key={insight.id}
                    className={`p-3 rounded-md border text-sm cursor-pointer transition-colors ${
                      insight.id === selectedInsight.id ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                    }`}
                    onClick={() => handleSelectInsight(insight)}
                  >
                    <div className="text-xs text-gray-500 mb-1">
                      {moment(insight.createdAt).fromNow()} ago by {insight.generatedBy}
                    </div>
                    <div
                      className="text-gray-700 whitespace-pre-wrap"
                      style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      {insight.content}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserInsights;
